import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { assistanceApi } from '../api/assistanceApi';
import { isAssistanceEligibleDuration } from '../types/assistance.types';

/**
 * React Query REFUSE un `enabled` défini mais non booléen, et fait tomber tout
 * l'écran. Or les drapeaux de la configuration effective arrivent de l'API en
 * `tinyint` — `1` ou `0`, jamais un vrai booléen, malgré ce qu'annoncent les types.
 * Un `0` transmis tel quel court-circuitait même une chaîne de `&&` et ressortait
 * inchangé. On coerce donc à l'entrée de chaque hook plutôt que de compter sur la
 * discipline des appelants.
 */
export const asFlag = (value: unknown): boolean => !!value;

/**
 * Référentiel des formules. Quatre lignes quasi immuables : on le garde longtemps
 * en cache plutôt que de le redemander à chaque ouverture d'un formulaire.
 */
export const useAssistanceFormulas = (enabled: unknown = true) =>
  useQuery({
    queryKey: ['assistance', 'formulas'],
    queryFn: assistanceApi.getFormulas,
    enabled: asFlag(enabled),
    staleTime: 30 * 60 * 1000,
  });

/**
 * Grille des prix d'achat Mutuaide. Confidentielle : l'API la refuse aux externes.
 * Seule la page d'administration des tarifs d'assistance la lit.
 */
export const usePurchasePriceGrid = (enabled: unknown = true) =>
  useQuery({
    queryKey: ['assistance', 'purchase-prices'],
    queryFn: assistanceApi.getPurchasePriceGrid,
    enabled: asFlag(enabled),
    staleTime: 5 * 60 * 1000,
  });

export const useSchedulePurchasePrice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assistanceApi.schedulePurchasePrice,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assistance', 'purchase-prices'] }),
  });
};

export const useDeletePurchasePrice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assistanceApi.deletePurchasePrice,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assistance', 'purchase-prices'] }),
  });
};

/**
 * Texte de l'encart des CP. Comme le référentiel des formules : une ligne, quasi
 * immuable, gardée longtemps en cache.
 */
export const useAssistanceArticle = (enabled: unknown = true) =>
  useQuery({
    queryKey: ['assistance', 'article'],
    queryFn: assistanceApi.getArticle,
    enabled: asFlag(enabled),
    staleTime: 30 * 60 * 1000,
  });

export const useUpdateAssistanceArticle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assistanceApi.updateArticle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistance', 'article'] });
    },
  });
};

/**
 * Tarifs de toutes les formules pour une société et une durée.
 *
 * Désactivé tant que la durée n'est pas éligible : inutile d'interroger le serveur
 * pendant que le vendeur passe par une durée de 3 mois.
 */
export const useAssistancePrices = (
  societyId: string | undefined,
  duration: number | undefined,
  enabled: unknown = true,
) =>
  useQuery({
    queryKey: ['assistance', 'prices', societyId, duration],
    queryFn: () => assistanceApi.getPrices(societyId!, duration!),
    enabled:
      asFlag(enabled) && !!societyId && isAssistanceEligibleDuration(duration),
    staleTime: 5 * 60 * 1000,
  });

/** Matrice des tarifs résolus d'une société, avec la source de chaque case. */
export const useAssistanceSocietyPricing = (
  societyId: string | undefined,
  enabled: unknown = true,
) =>
  useQuery({
    queryKey: ['assistance', 'society-pricing', societyId],
    queryFn: () => assistanceApi.getSocietyPricing(societyId!),
    enabled: asFlag(enabled) && !!societyId,
  });

/**
 * Invalidation commune aux mutations tarifaires.
 *
 * Trois clés à purger, pas une : la matrice de la fiche société, mais aussi les
 * tarifs résolus que sert le paramétrage de garantie — ils ont un `staleTime` de
 * 5 minutes et continueraient sinon à proposer l'ancien prix.
 */
function useInvalidateAssistancePricing() {
  const queryClient = useQueryClient();
  return (societyId: string) => {
    queryClient.invalidateQueries({ queryKey: ['assistance', 'society-pricing', societyId] });
    queryClient.invalidateQueries({ queryKey: ['assistance', 'prices'] });
  };
}

export const useCreateAssistanceCustomPrice = () => {
  const invalidate = useInvalidateAssistancePricing();
  return useMutation({
    mutationFn: ({
      societyId,
      ...payload
    }: {
      societyId: string;
      formulaId: number;
      duration: number;
      price: number;
    }) => assistanceApi.createCustomPrice(societyId, payload),
    onSuccess: (_data, variables) => invalidate(variables.societyId),
  });
};

export const useDeleteAssistanceCustomPrice = () => {
  const invalidate = useInvalidateAssistancePricing();
  return useMutation({
    mutationFn: ({ id }: { id: number; societyId: string }) =>
      assistanceApi.deleteCustomPrice(id),
    onSuccess: (_data, variables) => invalidate(variables.societyId),
  });
};
